import React, { useState } from 'react';
import { supabase } from '../../../../services/supabaseClient';
import { ShieldCheck, PenTool, Cpu, Check, AlertCircle } from 'lucide-react';

export default function SignatureModule({ appId, role, onSigned }: any) {
  const [loading, setLoading] = useState(false);
  const [iaVerified, setIaVerified] = useState(false);
  const [accepted, setAccepted] = useState(false);

  // APPEL RÉEL À L'IA
  const handleIAVerification = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('habitat-final-seal', {
        body: { appId, action: 'verify_identity' }
      });
      if (error) throw error;
      setIaVerified(true);
    } catch (err) {
      alert("La vérification IA a échoué. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  const handleFinalSign = async () => {
    if (!accepted || !iaVerified) return;
    setLoading(true);

    try {
      // 1. Capture des preuves légales
      const ipRes = await fetch('https://api.ipify.org?format=json');
      const { ip } = await ipRes.json();

      const signatureData = role === 'tenant' 
        ? { tenant_signed_at: new Date().toISOString(), tenant_signature_ip: ip }
        : { landlord_signed_at: new Date().toISOString(), landlord_signature_ip: ip };

      // 2. Signature en base de données
      const { error: signError } = await supabase.from('habitat_applications').update(signatureData).eq('id', appId);
      if (signError) throw signError;

      // 3. Déclenchement de l'email final si le contrat est complet
      // On vérifie si l'autre partie a déjà signé
      const { data: app } = await supabase.from('habitat_applications').select('*').eq('id', appId).single();
      
      if (app.tenant_signed_at && app.landlord_signed_at) {
        await supabase.functions.invoke('habitat-final-seal', {
          body: { appId, action: 'send_final_contract' }
        });
      }

      onSigned(); // Rafraîchir la page du bail
    } catch (err) {
      alert("Erreur lors de la signature finale.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-slate-900 text-white rounded-[2.5rem] p-8 md:p-12 mt-12 shadow-2xl border border-slate-800">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-10">
        <div>
          <h4 className="text-2xl font-black mb-2 flex items-center gap-3">
            <PenTool className="text-teal-400" /> Signature Officielle
          </h4>
          <p className="text-slate-400 font-medium">Certification sécurisée du bail Forfeo.</p>
        </div>
        <div className={`px-4 py-2 rounded-full border flex items-center gap-2 text-[10px] font-black uppercase tracking-widest ${iaVerified ? 'bg-teal-500/10 border-teal-500/50 text-teal-400' : 'bg-blue-500/10 border-blue-500/50 text-blue-400 animate-pulse'}`}>
          <Cpu size={14} /> {iaVerified ? 'Identité Validée par Forfeo IA' : 'Vérification IA en attente'}
        </div>
      </div>

      {!iaVerified ? (
        <button 
          onClick={handleIAVerification}
          disabled={loading}
          className="w-full bg-blue-600 hover:bg-blue-500 text-white py-6 rounded-2xl font-black uppercase tracking-widest transition-all flex items-center justify-center gap-3 shadow-xl"
        >
          {loading ? 'Analyse des pièces d\'identité...' : 'Lancer la vérification IA'}
        </button>
      ) : (
        <div className="space-y-6 animate-in slide-in-from-bottom-4">
          <div className="bg-white/5 p-6 rounded-2xl border border-white/10 flex items-start gap-4">
            <input 
              type="checkbox" 
              id="final-accept"
              checked={accepted} 
              onChange={(e) => setAccepted(e.target.checked)}
              className="mt-1 w-6 h-6 rounded border-slate-700 text-teal-500 bg-slate-800 cursor-pointer"
            />
            <label htmlFor="final-accept" className="text-sm text-slate-300 font-bold leading-relaxed cursor-pointer">
              Je confirme que les informations sont exactes et j'appose ma signature électronique avec valeur légale. Cette action scellera le contrat.
            </label>
          </div>

          <button
            onClick={handleFinalSign}
            disabled={!accepted || loading}
            className={`w-full py-6 rounded-2xl font-black uppercase tracking-widest transition-all flex items-center justify-center gap-3 ${accepted ? 'bg-teal-500 text-slate-900 shadow-[0_0_30px_rgba(20,184,166,0.4)]' : 'bg-slate-800 text-slate-500 cursor-not-allowed'}`}
          >
            {loading ? 'Génération du contrat scellé...' : <><ShieldCheck size={22}/> Signer et Sceller le bail</>}
          </button>
        </div>
      )}

      <p className="text-[9px] text-slate-600 text-center mt-8 font-black uppercase tracking-[0.2em]">
        Système de protection Forfeo Secure ID • Conformité Loi 25
      </p>
    </div>
  );
}
